import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight, Copy, CheckCircle2, Tag } from "lucide-react";

/**
 * "Featured deals" rotating strip for the landing page.
 *
 * Pulls the top ~8 active marketing codes with hero photos from
 * `GET /api/marketing-codes/featured`. Renders as an auto-advancing
 * card carousel; a click on any card copies the promo code and a
 * user-can-swipe strip below shows all active deals as small chips.
 * Silently hides itself when the backend returns an empty list so
 * an unpopulated deals table never leaves an empty section behind.
 */
export default function LandingFeaturedDealsStrip() {
  const [deals, setDeals] = useState([]);
  const [idx, setIdx] = useState(0);
  const [copied, setCopied] = useState(null);
  const timerRef = useRef(null);
  const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

  useEffect(() => {
    axios.get(`${backend}/api/marketing-codes/featured`)
      .then(({ data }) => setDeals(Array.isArray(data?.deals) ? data.deals : []))
      .catch(() => setDeals([]));
  }, [backend]);

  const advance = useCallback((dir) => {
    setIdx((prev) => {
      const n = deals.length || 1;
      return ((prev + dir) % n + n) % n;
    });
  }, [deals.length]);

  // Auto-advance every 6s. Pauses when the tab is hidden (avoids the
  // "why is my scroll jumping" surprise when the user comes back).
  useEffect(() => {
    if (deals.length < 2) return;
    const tick = () => advance(1);
    timerRef.current = setInterval(tick, 6000);
    return () => clearInterval(timerRef.current);
  }, [deals.length, advance]);

  if (deals.length === 0) return null;
  const current = deals[idx] || deals[0];
  const imgSrc = current.image_url.startsWith("http")
    ? current.image_url
    : `${backend}${current.image_url}`;

  function copyCode(code) {
    try {
      navigator.clipboard.writeText(code);
      setCopied(code);
      setTimeout(() => setCopied(null), 1600);
    } catch { /* ignore — old browser */ }
  }

  return (
    <section className="py-10 bg-slate-50 border-y border-slate-200" data-testid="landing-featured-deals-strip">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-4 gap-3">
          <div>
            <p className="text-[11px] font-black uppercase tracking-widest text-purple-600">Featured Deals</p>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Save on this month&apos;s promos
            </h2>
          </div>
          {deals.length > 1 && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => advance(-1)}
                className="p-2 rounded-full border border-slate-200 bg-white hover:bg-slate-100 transition-colors"
                aria-label="Previous deal"
                data-testid="landing-featured-prev"
              >
                <ChevronLeft className="w-4 h-4 text-slate-600" />
              </button>
              <button
                onClick={() => advance(1)}
                className="p-2 rounded-full border border-slate-200 bg-white hover:bg-slate-100 transition-colors"
                aria-label="Next deal"
                data-testid="landing-featured-next"
              >
                <ChevronRight className="w-4 h-4 text-slate-600" />
              </button>
            </div>
          )}
        </div>

        {/* Hero card — the currently featured deal, big and clickable */}
        <div className="rounded-3xl overflow-hidden bg-white border border-slate-200 shadow-sm">
          <div className="grid md:grid-cols-2 gap-0">
            <div className="relative h-56 md:h-72 bg-slate-100">
              <img
                src={imgSrc}
                alt={current.description}
                className="w-full h-full object-cover"
                data-testid="landing-featured-image"
              />
              <div className="absolute top-3 left-3 bg-emerald-600 text-white text-xs font-black uppercase tracking-wider px-3 py-1 rounded-full">
                ${current.amount} off
              </div>
            </div>
            <div className="p-6 flex flex-col justify-center">
              <p className="text-xs uppercase tracking-widest text-purple-600 font-black mb-2">Limited time</p>
              <h3 className="text-xl sm:text-2xl font-extrabold text-slate-900 mb-2" data-testid="landing-featured-title">
                {current.description}
              </h3>
              <p className="text-sm text-slate-500 mb-4">
                Expires {new Date(current.expires_at).toLocaleDateString(undefined, { month: "long", day: "numeric" })}
              </p>
              <div className="flex items-center gap-2 mb-4">
                <span className="flex-1 font-mono text-lg font-black tracking-widest bg-slate-100 border-2 border-dashed border-slate-300 rounded-lg px-4 py-3 text-slate-900" data-testid="landing-featured-code">
                  {current.code}
                </span>
                <button
                  onClick={() => copyCode(current.code)}
                  className="px-4 py-3 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-bold text-sm inline-flex items-center gap-2 transition-colors"
                  data-testid="landing-featured-copy"
                >
                  {copied === current.code ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied === current.code ? "Copied" : "Copy"}
                </button>
              </div>
              <p className="text-xs text-slate-500">Apply at checkout — stacks with your subscription savings.</p>
            </div>
          </div>
        </div>

        {/* Secondary chip strip — every active deal at a glance */}
        {deals.length > 1 && (
          <div className="flex gap-2 mt-4 overflow-x-auto pb-1 -mx-1 px-1" data-testid="landing-featured-chips">
            {deals.map((d, i) => (
              <button
                key={d.id}
                onClick={() => setIdx(i)}
                className={`shrink-0 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border transition-colors text-xs font-bold ${
                  i === idx
                    ? "bg-purple-600 border-purple-600 text-white"
                    : "bg-white border-slate-200 text-slate-700 hover:bg-slate-100"
                }`}
                data-testid={`landing-featured-chip-${d.id}`}
              >
                <Tag className="w-3 h-3" />
                {d.code} · ${d.amount}
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
